import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@a7y/tailwind-catalyst";
import type { ContentSection } from "@a7y/types";
import { DOCS_SLUG_REGEX } from "@a7y/types";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { isImageAllowed } from "../utils";

const BLOG_SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

interface ImageUploaderProps {
  /** Defaults to "blog" so every pre-existing caller is unaffected. */
  section?: ContentSection;
  /** Post/page slug — forms the `<section>/<slug>/` R2 key prefix (`docs/` for
   *  docs, per plan T2a `imagePrefix`). Upload is gated on it being valid. */
  slug: string;
  /** Currently-selected image URL, if any. */
  value?: string;
  /** Emitted with the public URL once an image is uploaded or picked. */
  onSelect: (url: string) => void;
  /** Optional clear handler (e.g. remove hero image). */
  onClear?: () => void;
  className?: string;
}

/**
 * R2 image upload + existing-image picker (A7Y-2068; generalised to both
 * sections at A7Y-4470). Uploads go through the multipart escape hatch
 * (`api.<section>.uploadImage`) with a client-side MIME/size pre-check and a
 * 409 overwrite-confirm flow. Reused for the blog hero image and the inline
 * image insert in both sections' body editor.
 */
export function ImageUploader({
  section = "blog",
  slug,
  value,
  onSelect,
  onClear,
  className,
}: ImageUploaderProps) {
  const sectionApi = section === "docs" ? api.docs : api.blog;
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  // When a 409 is returned, hold the file so the user can confirm overwrite.
  const [pendingOverwrite, setPendingOverwrite] = useState<{
    file: File;
    key: string;
  } | null>(null);

  // Docs slugs are multi-segment paths (`audits/run-an-audit`); blog slugs are
  // a single kebab-case segment — each section validates against its own
  // canonical accessor (`DOCS_SLUG_REGEX` from `@a7y/types`) rather than a
  // second, re-derived pattern.
  const slugReady = (section === "docs" ? DOCS_SLUG_REGEX : BLOG_SLUG_REGEX).test(slug);

  const imagesQuery = useQuery({
    queryKey: [section, "images"],
    queryFn: () => sectionApi.listImages(),
    enabled: showPicker,
  });

  async function doUpload(file: File, confirmOverwrite: boolean) {
    setUploading(true);
    setError(null);
    try {
      const res = await sectionApi.uploadImage(file, slug, { confirmOverwrite });
      if (res.ok) {
        setPendingOverwrite(null);
        onSelect(res.data.url);
        return;
      }
      if (res.status === 409) {
        setPendingOverwrite({ file, key: res.key ?? "" });
        return;
      }
      setError(res.error);
    } finally {
      setUploading(false);
    }
  }

  function handleFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    setPendingOverwrite(null);
    const check = isImageAllowed(file);
    if (!check.ok) {
      setError(check.error ?? "Unsupported image");
      return;
    }
    if (!slugReady) {
      setError(
        section === "docs"
          ? "Set a valid page slug before uploading images."
          : "Set a valid post slug before uploading images.",
      );
      return;
    }
    void doUpload(file, false);
  }

  return (
    <div className={cn("space-y-3", className)}>
      {value && (
        <div className="flex items-start gap-3">
          <img
            src={value}
            alt="Selected"
            className="h-24 w-40 rounded-lg border border-zinc-200 object-cover dark:border-zinc-700"
          />
          <div className="flex flex-col gap-1">
            <p className="max-w-xs truncate text-xs text-zinc-500 dark:text-zinc-400">
              {value}
            </p>
            {onClear && (
              <button
                type="button"
                onClick={onClear}
                className="self-start text-xs font-medium text-red-600 hover:underline dark:text-red-400"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <Button
          type="button"
          outline
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        >
          {uploading ? "Uploading…" : value ? "Replace image" : "Upload image"}
        </Button>
        <Button
          type="button"
          plain
          onClick={() => setShowPicker((s) => !s)}
        >
          {showPicker ? "Hide library" : "Choose from library"}
        </Button>
      </div>

      {!slugReady && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Uploads are stored under <code>{section}/&lt;slug&gt;/</code> — set a
          valid slug first.
        </p>
      )}

      {pendingOverwrite && (
        <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          <p>
            An image named{" "}
            <span className="font-mono">{pendingOverwrite.key}</span> already
            exists. Overwrite it?
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              color="amber"
              disabled={uploading}
              onClick={() => doUpload(pendingOverwrite.file, true)}
            >
              {uploading ? "Overwriting…" : "Overwrite"}
            </Button>
            <Button
              type="button"
              plain
              onClick={() => setPendingOverwrite(null)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {showPicker && (
        <div className="rounded-lg border border-zinc-200 p-3 dark:border-zinc-700">
          {imagesQuery.isLoading ? (
            <p className="text-sm text-zinc-500">Loading library…</p>
          ) : imagesQuery.data && imagesQuery.data.urls.length > 0 ? (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {imagesQuery.data.urls.map((url) => (
                <button
                  type="button"
                  key={url}
                  onClick={() => {
                    onSelect(url);
                    setShowPicker(false);
                  }}
                  className={cn(
                    "group relative overflow-hidden rounded-md border transition-colors",
                    url === value
                      ? "border-blue-500 ring-2 ring-blue-500"
                      : "border-zinc-200 hover:border-blue-400 dark:border-zinc-700",
                  )}
                >
                  <img
                    src={url}
                    alt=""
                    className="h-20 w-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-500">No uploaded images yet.</p>
          )}
        </div>
      )}
    </div>
  );
}
